import type { IBillItem, IBillItemOption } from "@/data/model/Bill";
import { Format } from "@/data/util/Format";

import { clientLanguage } from "@/shared/localization/language";
import { Ripple } from "@/shared/ui/Ripple";

export class PaidLineItem {
    public $element: JQuery;
    private readonly $text: JQuery;
    private readonly $variants: JQuery;
    private readonly $quantity: JQuery;

    public model?: IBillItem;
    private paidQty = 0;
    public isFullyPaid = false;

    constructor() {
        this.$element = $('<div class="lineitem selectable" />');
        Ripple.bindRippleOut(this.$element, undefined, 0.18);

        const inner = $('<div class="inner" />').appendTo(this.$element);

        this.$quantity = $('<div class="quantity">').appendTo(inner);

        const container = $('<div class="textcontainer" />').appendTo(inner);

        this.$text = $('<div class="text" />').appendTo(container);
        this.$variants = $('<div class="variants" />').appendTo(container);

        this.$element.click((e) => {
            e.preventDefault();

            if (this.isFullyPaid) return;
        });
    }

    public update(model: IBillItem): PaidLineItem {
        this.$text.empty();
        this.$variants.empty();
        this.$quantity.empty();

        this.model = model;
        this.calcRemainingQuantity();
        this.$element.data("sortOrder", this.model.sortOrder);

        if (model.name !== undefined) {
            const itmPrice = this.paidQty * model.unitPrice;

            this.$text.append('<span class="col name">' + Format.translate(model.name, clientLanguage()) + "</span>");
            this.$quantity.append(this.paidQty + "x");
            this.$text.append(
                '<span class="col paidlineprice">' +
                    (itmPrice != null && itmPrice > 0 ? itmPrice.toFixed(2) : "") +
                    "</span>"
            );
        }

        if (model.options.length > 0) {
            this.$variants.show();
            this.recursiveAppendOptions(this.$variants, model, this.paidQty);
        } else {
            this.$variants.hide();
        }

        if (this.paidQty <= 0) {
            this.$element.addClass("complete");
        } else {
            this.$element.removeClass("complete");
        }

        return this;
    }

    private recursiveAppendOptions($container: JQuery, item: IBillItem, multiplier: number) {
        if (item.options.length === 0) return;

        const subContainer = $('<div class="subs" />').appendTo($container);

        for (const i in item.options) {
            const optionGroup = item.options[i];
            const groupContainer = $(
                '<div class="group"><div class="name">' +
                    Format.htmlEscape(Format.translate(optionGroup.name, clientLanguage())) +
                    "</div></div>"
            ).appendTo(subContainer);
            const optionsContainer = $('<div class="entries" />').appendTo(groupContainer);

            for (const ai in optionGroup.items) {
                const optionContainer = $('<div class="item" />').appendTo(optionsContainer);
                const optionHead = $('<div class="head" />').appendTo(optionContainer);

                const option = optionGroup.items[ai];
                const nextMultiplier = multiplier * option.quantity;

                optionHead.append(
                    '<div class="name">' +
                        (option.quantity === 1 ? "" : '<div class="quantity">' + option.quantity + "x</div>") +
                        Format.htmlEscape(Format.translate(option.name, clientLanguage())) +
                        "</div>"
                );
                optionHead.append(
                    '<div class="paidprice">' +
                        (option.unitPrice === 0 ? "" : (nextMultiplier * option.unitPrice).toFixed(2)) +
                        "</div>"
                );

                this.recursiveAppendItemOptions(optionContainer, option, nextMultiplier);
            }
        }
    }

    private recursiveAppendItemOptions($container: JQuery, item: IBillItemOption, multiplier: number) {
        if (item.options.length === 0) return;

        const subContainer = $('<div class="subs" />').appendTo($container);

        for (const i in item.options) {
            const optionGroup = item.options[i];
            const groupContainer = $(
                '<div class="group"><div class="name">' +
                    Format.htmlEscape(Format.translate(optionGroup.name, clientLanguage())) +
                    "</div></div>"
            ).appendTo(subContainer);
            const optionsContainer = $('<div class="entries" />').appendTo(groupContainer);

            for (const ai in optionGroup.items) {
                const optionContainer = $('<div class="item" />').appendTo(optionsContainer);
                const optionHead = $('<div class="head" />').appendTo(optionContainer);

                const option = optionGroup.items[ai];
                const nextMultiplier = multiplier * option.quantity;

                optionHead.append(
                    '<div class="name">' +
                        (option.quantity === 1 ? "" : '<div class="quantity">' + option.quantity + "x</div>") +
                        Format.htmlEscape(Format.translate(option.name, clientLanguage())) +
                        "</div>"
                );
                optionHead.append(
                    '<div class="paidprice">' +
                        (option.unitPrice === 0 ? "" : (nextMultiplier * option.unitPrice).toFixed(2)) +
                        "</div>"
                );

                this.recursiveAppendItemOptions(optionContainer, option, nextMultiplier);
            }
        }
    }

    public remove() {
        this.$element.slideUp(300, "easeOutExpo", () => {
            this.$element.remove();
        });
    }

    private calcRemainingQuantity() {
        if (this.model === undefined) {
            this.paidQty = 0;
            this.isFullyPaid = false;
            return;
        }

        const paidQty = this.model.paidQuantity;
        this.isFullyPaid = paidQty >= this.model.quantity;
        this.paidQty = paidQty;
    }

    public appendTo($element: JQuery): PaidLineItem {
        this.$element.appendTo($element);

        return this;
    }
}
